import { motion } from "framer-motion";
import { BookOpen, ClipboardList, Calculator, ArrowRight } from "lucide-react";
import ScrambleText from "./ScrambleText";
import MoleculeDraw from "./MoleculeDraw";
import { ParallaxShader, ParallaxSpan, PointerLayer } from "./Parallax";
import { usePageTransition } from "./PageTransition";
import { buzz } from "../lib/haptics";

const resources = [
  {
    id: "library",
    Icon: BookOpen,
    title: "Education Library",
    desc: "Plain-English breakdowns of every peptide — mechanism, half-life, research and real-world context, stripped of the hype.",
  },
  {
    id: "protocols",
    Icon: ClipboardList,
    title: "Research Protocols",
    desc: "Structured, reference-backed protocol frameworks to help you understand how studies are designed and interpreted.",
  },
  {
    id: "calculator",
    Icon: Calculator,
    title: "Dosing Calculator",
    desc: "Reconstitution and syringe-draw math handled for you in the Lab — accurate figures for any vial, dose and syringe.",
    to: "/lab",
  },
];

export default function Science() {
  const transition = usePageTransition();

  const openLab = () => {
    buzz(12);
    transition ? transition.go("/lab") : (window.location.href = "/lab");
  };

  return (
    <section id="science" data-testid="science" className="relative overflow-hidden border-y border-[#141414] bg-[#080808]">
      <ParallaxShader variant="ink" intensity={0.9} strength={30} />
      {/* Marquee */}
      <div className="relative z-10 border-b border-[#141414] py-6">
        <div className="marquee-track">
          {[0, 1].map((k) => (
            <span key={k} className="font-display flex items-center text-4xl font-black tracking-tight text-white/[0.08] md:text-6xl">
              PEPTIDE EDUCATION
              <span className="mx-6 text-[#d91616]">◆</span>
              RESEARCH RESOURCES
              <span className="mx-6 text-[#d91616]">◆</span>
              峠 TOJI PEPTIDES
              <span className="mx-6 text-[#d91616]">◆</span>
            </span>
          ))}
        </div>
      </div>

      <div className="relative mx-auto max-w-[1400px] px-6 py-28 md:px-12 md:py-40">
        <ParallaxSpan className="ambient-red" style={{ width: "38rem", height: "38rem", top: "0", left: "-12rem" }} strength={50} />
        <ParallaxSpan className="section-kanji font-jp bottom-10 right-6 text-[12rem] md:text-[20rem]" strength={95}>
          知
        </ParallaxSpan>
        <div className="relative z-10 grid grid-cols-1 items-start gap-16 md:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <ScrambleText
              as="span"
              text="WHAT IS TOJI"
              className="font-mono2 mb-4 block text-xs uppercase tracking-[0.4em] text-[#d91616]"
            />
            <PointerLayer strength={-16}>
              <h2 className="text-glow-hover font-display text-4xl font-black leading-[0.95] tracking-tighter md:text-6xl">
                KNOWLEDGE
                <br />
                IS THE
                <br />
                <span className="text-[#d91616]">SHARPEST BLADE.</span>
              </h2>
            </PointerLayer>
            <p className="mt-8 max-w-lg font-body text-sm leading-relaxed text-white/60 md:text-base">
              Toji Peptides is an <span className="text-white">education-first resource</span> — a
              knowledge base, not a storefront. Our purpose is to translate dense clinical research into
              clear, honest explanations of what each peptide is, how it behaves in the body, and what the
              evidence actually says.
            </p>
            <p className="mt-4 max-w-lg font-body text-sm leading-relaxed text-white/45 md:text-base">
              Alongside the library, we build practical tools that keep research grounded in accuracy —
              structured protocol references and a precision dosing calculator — so curiosity is always
              backed by understanding.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col gap-4"
          >
            {resources.map(({ id, Icon, title, desc, to }) => {
              const clickable = Boolean(to);
              return (
                <div
                  key={id}
                  data-testid={`resource-${id}`}
                  onClick={clickable ? openLab : undefined}
                  role={clickable ? "button" : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onKeyDown={clickable ? (e) => (e.key === "Enter" || e.key === " ") && openLab() : undefined}
                  className={`group relative overflow-hidden rounded-xl border border-[#1c1c1c] bg-[#0a0a0a] p-6 transition-colors hover:border-[#d91616]/50 hover:bg-[#0f0f0f] md:p-7 ${
                    clickable ? "cursor-pointer" : ""
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-lg border border-[#d91616]/40 text-[#d91616] transition-colors group-hover:bg-[#d91616] group-hover:text-white">
                      <Icon size={20} />
                    </span>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-display text-lg font-black tracking-tight text-white md:text-xl">
                          {title}
                        </h3>
                        {clickable && (
                          <ArrowRight
                            size={16}
                            className="text-[#d91616] transition-transform group-hover:translate-x-1"
                          />
                        )}
                      </div>
                      <p className="mt-2 font-body text-sm leading-relaxed text-white/50">
                        {desc}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </motion.div>
        </div>
      </div>

      <MoleculeDraw />
    </section>
  );
}
